import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { estadoDelPrestamo } from '../utils/prestamo';

const DIAS_POR_PERIODO = { daily: 1, weekly: 7, biweekly: 15, monthly: 30 };

// El cobradiario no lleva fecha de vencimiento escrita, y sin embargo la tiene:
// termina cuando entrega la última cuota. Se deduce de cuántas son, que ya lo
// sabe el motor —no se vuelve a calcular aquí, que es como las cuentas se
// separan—. Sin esto, todos los préstamos de cuota fija caían en «sin fecha» y
// esta pantalla no los contaba: el dueño no veía vencer a la mitad de su cartera.
function finDeCuotaFija(prestamo) {
  const e = estadoDelPrestamo(prestamo, [], new Date());
  if (!e.cuotasTotales) return null;
  const dias = DIAS_POR_PERIODO[prestamo.frequency] || 30;
  const inicio = new Date(prestamo.start_date);
  if (isNaN(inicio.getTime())) return null;
  return new Date(inicio.getTime() + e.cuotasTotales * dias * 86400000);
}

function OverdueStats() {
  const [stats, setStats] = useState({
    total: 0,
    overdue: 0,
    warning: 0,
    active: 0,
    overdueAmount: 0,
    warningAmount: 0,
    totalAtRisk: 0
  });
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [loansSinFecha, setLoansSinFecha] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      // Una sola petición con todos los préstamos. Antes se pedían cliente a
      // cliente: con 114 clientes eran 114 llamadas encadenadas, unos 30
      // segundos de espera.
      const { data } = await axios.get('/api/loans');
      const loans = data || [];

      let totalLoans = 0;
      let overdueLoans = 0;
      let warningLoans = 0;
      let activeLoans = 0;
      let overdueAmount = 0;
      let warningAmount = 0;
      let sinFecha = 0;

      const monthlyData = {};
      const today = new Date();

      for (const loan of loans) {
        // Un préstamo sin fecha de vencimiento no puede clasificarse. Se
        // cuentan aparte para poder avisar en pantalla en vez de ignorarlos.
        const maturityDate = loan.maturity_date
          ? new Date(loan.maturity_date)
          : (loan.modalidad === 'cuota_fija' ? finDeCuotaFija(loan) : null);

        if (!maturityDate || isNaN(maturityDate.getTime())) {
          sinFecha++;
          continue;
        }
        const daysUntil = Math.ceil((maturityDate - today) / (1000 * 60 * 60 * 24));

        totalLoans++;

        if (daysUntil < 0) {
          overdueLoans++;
          overdueAmount += loan.amount;
        } else if (daysUntil <= 7) {
          warningLoans++;
          warningAmount += loan.amount;
        } else {
          activeLoans++;
        }

        // Agrupar por mes de vencimiento
        const monthKey = maturityDate.toLocaleDateString('es-CO', { year: 'numeric', month: 'long' });
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = { count: 0, amount: 0 };
        }
        monthlyData[monthKey].count++;
        monthlyData[monthKey].amount += loan.amount;
      }

      setLoansSinFecha(sinFecha);

      setStats({
        total: totalLoans,
        overdue: overdueLoans,
        warning: warningLoans,
        active: activeLoans,
        overdueAmount,
        warningAmount,
        totalAtRisk: overdueLoans + warningLoans
      });

      setMonthlyStats(Object.entries(monthlyData).map(([month, data]) => ({
        month,
        count: data.count,
        amount: data.amount
      })));

      setLoading(false);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setLoading(false);
    }
  };

  const riskPercentage = stats.total > 0 ? Math.round((stats.totalAtRisk / stats.total) * 100) : 0;

  return (
    <div className="overdue-stats-page">
      <h2>📋 Resumen de vencimientos</h2>

      {/* Sin fecha de vencimiento no hay nada que clasificar. Se avisa en vez
          de mostrar ceros sin explicación. */}
      {!loading && loansSinFecha > 0 && (
        <div style={{
          padding: '15px', borderRadius: '8px', marginBottom: '20px',
          background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: '14px'
        }}>
          ⚠️ <strong>{loansSinFecha} préstamos sin fecha de vencimiento</strong> no aparecen
          en estas cifras. Los préstamos creados antes de que existiera ese campo no la
          tienen, y sin ella no se puede saber si están vencidos.
        </div>
      )}

      {loading ? (
        <p className="empty-state">Cargando estadísticas...</p>
      ) : (
        <>
          {/* KPI Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '30px' }}>
            {/* Total */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '25px',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>
                {stats.total}
              </div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>Total Préstamos</div>
            </div>

            {/* Vencidos */}
            <div style={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              padding: '25px',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>
                🔴 {stats.overdue}
              </div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>Vencidos</div>
              <div style={{ fontSize: '12px', marginTop: '8px', opacity: 0.8 }}>
                ${(stats.overdueAmount / 1000).toFixed(0)}K en riesgo
              </div>
            </div>

            {/* Por Vencer */}
            <div style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              padding: '25px',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>
                🟡 {stats.warning}
              </div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>Por Vencer (7 días)</div>
              <div style={{ fontSize: '12px', marginTop: '8px', opacity: 0.8 }}>
                ${(stats.warningAmount / 1000).toFixed(0)}K en atención
              </div>
            </div>

            {/* Vigentes */}
            <div style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              padding: '25px',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>
                🟢 {stats.active}
              </div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>Vigentes</div>
              <div style={{ fontSize: '12px', marginTop: '8px', opacity: 0.8 }}>
                En buen estado
              </div>
            </div>
          </div>

          {/* Risk Meter */}
          <div style={{
            background: 'white',
            padding: '25px',
            borderRadius: '12px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            marginBottom: '30px',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>⚠️ Indicador de Riesgo</h3>

            <div style={{ marginBottom: '15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontWeight: 'bold' }}>Préstamos en Riesgo</span>
                <span style={{ fontWeight: 'bold', color: riskPercentage > 30 ? '#ef4444' : riskPercentage > 15 ? '#f59e0b' : '#10b981' }}>
                  {riskPercentage}%
                </span>
              </div>

              <div style={{
                width: '100%',
                height: '24px',
                background: '#e5e7eb',
                borderRadius: '12px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${riskPercentage}%`,
                  height: '100%',
                  background: riskPercentage > 30 ? '#ef4444' : riskPercentage > 15 ? '#f59e0b' : '#10b981',
                  transition: 'width 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  {riskPercentage > 10 && `${riskPercentage}%`}
                </div>
              </div>

              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                {stats.totalAtRisk} de {stats.total} préstamos requieren atención
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '15px',
              marginTop: '20px'
            }}>
              <div style={{ background: '#fef2f2', padding: '15px', borderRadius: '8px', borderLeft: '4px solid #ef4444' }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#ef4444' }}>🔴 {stats.overdue}</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>Vencidos ahora</div>
              </div>

              <div style={{ background: '#fffbeb', padding: '15px', borderRadius: '8px', borderLeft: '4px solid #f59e0b' }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#f59e0b' }}>🟡 {stats.warning}</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>Por vencer pronto</div>
              </div>

              <div style={{ background: '#f0fdf4', padding: '15px', borderRadius: '8px', borderLeft: '4px solid #10b981' }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#10b981' }}>🟢 {stats.active}</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>En buen estado</div>
              </div>
            </div>
          </div>

          {/* Monthly Schedule */}
          <div style={{
            background: 'white',
            padding: '25px',
            borderRadius: '12px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>📅 Calendario de Vencimientos</h3>

            {monthlyStats.length === 0 ? (
              <p style={{ color: '#6b7280', margin: 0 }}>No hay vencimientos registrados</p>
            ) : (
              <div style={{ display: 'grid', gap: '12px' }}>
                {monthlyStats.map((month, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px',
                    background: '#f9fafb',
                    borderRadius: '8px',
                    borderLeft: '4px solid #667eea'
                  }}>
                    <div>
                      <div style={{ fontWeight: 'bold', color: '#1f2937' }}>{month.month}</div>
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>{month.count} préstamo(s)</div>
                    </div>
                    <div style={{
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: '#667eea',
                      background: '#eef2ff',
                      padding: '8px 12px',
                      borderRadius: '6px'
                    }}>
                      ${(month.amount / 1000).toFixed(0)}K
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default OverdueStats;
